import React from "react";

// Source
import StoreRoute from "../../store/Route";
import StoreDocumentPreview from "../../store/custom/Document";
import StoreSetting from "../../store/Setting";
import StorePreference from "../../store/Preference";
import ControllerAlert from "../Alert";
import * as HelperSrc from "../../HelperSrc";
import * as ModelHelperSrc from "../../model/HelperSrc";
import * as ModelDocument from "../../model/custom/Document";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelSetting from "../../model/Setting";
import * as ModelCustomSetting from "../../model/custom/Setting";
import * as ApiDocument from "../../api/custom/Document";
import * as ModelPreference from "../../model/Preference";
import * as ModelCustomPreference from "../../model/custom/Preference";

export default class ControllerDocumentPreview {
    // Variable
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeDocumentPreview: ModelDocument.Istore;
    private storageDocumentPreviewData = React.useRef<ModelDocument.Idata | undefined>(undefined);
    private storeSetting: ModelSetting.Istore<ModelCustomSetting.Idata>;
    private storageSettingData = React.useRef<ModelCustomSetting.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelCustomPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelCustomPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementDetail = React.useRef<HTMLElement | null>(null);
    private elementClientName = React.useRef<HTMLInputElement | null>(null);
    private elementClientIndustry = React.useRef<HTMLInputElement | null>(null);
    private elementProjectName = React.useRef<HTMLInputElement | null>(null);
    private elementReportingYear = React.useRef<HTMLInputElement | null>(null);
    private elementBottom = React.useRef<HTMLElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private elementButtonDraft = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private selectOption = React.useRef<ModelDocument.IselectOption | undefined>(undefined);
    private validationForm = React.useRef<ModelDocument.Idata | undefined>(undefined);
    private isDialogOpened = React.useRef(false);
    private dataMain = React.useRef<ModelDocument.Idata | undefined>(undefined);
    private language = React.useRef<string>("");
    private timeoutDraft: NodeJS.Timeout | null = null;

    private isAtTest = React.useRef<boolean>(false);

    getStorageRoute: ModelCustomRoute.Idata | undefined;
    getStorageDocumentPreview: ModelDocument.Idata | undefined;
    getStorageSetting: ModelCustomSetting.Idata | undefined;

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getSelectOption: ModelDocument.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelDocument.IselectOption | undefined>>;

    getValidationForm: ModelDocument.Idata | undefined;
    setValidationForm: React.Dispatch<React.SetStateAction<ModelDocument.Idata | undefined>>;

    getIsDialogOpen: boolean;
    setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

    getUrlFile: string | undefined;
    setUrlFile: React.Dispatch<React.SetStateAction<string | undefined>>;

    getDataMain: ModelDocument.Idata | undefined;
    setDataMain: React.Dispatch<React.SetStateAction<ModelDocument.Idata | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getElementClientIndustry: HTMLInputElement | null;
    setElementClientIndustry: React.Dispatch<React.SetStateAction<HTMLInputElement | null>>;

    getElementReportingYear: HTMLInputElement | null;
    setElementReportingYear: React.Dispatch<React.SetStateAction<HTMLInputElement | null>>;

    getElementClientIndustryValue: string;
    setElementClientIndustryValue: React.Dispatch<React.SetStateAction<string>>;

    getElementFunctionValue: string[];
    setElementFunctionValue: React.Dispatch<React.SetStateAction<string[]>>;

    getElementRisk1Value: string;
    setElementRisk1Value: React.Dispatch<React.SetStateAction<string>>;

    getElementRisk2Value: string;
    setElementRisk2Value: React.Dispatch<React.SetStateAction<string>>;

    getElementRisk3Value: string;
    setElementRisk3Value: React.Dispatch<React.SetStateAction<string>>;

    getElementRisk4Value: string;
    setElementRisk4Value: React.Dispatch<React.SetStateAction<string>>;

    getElementRisk5Value: string;
    setElementRisk5Value: React.Dispatch<React.SetStateAction<string>>;

    getElementRisk6Value: string;
    setElementRisk6Value: React.Dispatch<React.SetStateAction<string>>;

    getElementRisk7Value: string;
    setElementRisk7Value: React.Dispatch<React.SetStateAction<string>>;

    getElementRisk8Value: string;
    setElementRisk8Value: React.Dispatch<React.SetStateAction<string>>;

    private detectIsAtTest = (): boolean => {
        if (typeof document === "undefined") {
            return false;
        }

        const atTestCookie = document.cookie
            .split(";")
            .map((item) => item.trim())
            .find((item) => item.startsWith("isAtTest="));

        if (atTestCookie) {
            return atTestCookie === "isAtTest=1";
        }

        const isWebDriver = typeof navigator !== "undefined" && navigator.webdriver === true;

        return isWebDriver;
    };

    // Method
    constructor(isRendered: boolean) {
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeDocumentPreview = new StoreDocumentPreview();
        this.storageDocumentPreviewData.current = this.storeDocumentPreview.storageGet();
        this.storeSetting = new StoreSetting();
        this.storageSettingData.current = this.storeSetting.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getStorageRoute] = React.useState<ModelCustomRoute.Idata | undefined>(this.storageRouteData.current);
        this.getStorageRoute = getStorageRoute;
        const [getStorageDocumentPreview] = React.useState<ModelDocument.Idata | undefined>(this.storageDocumentPreviewData.current);
        this.getStorageDocumentPreview = getStorageDocumentPreview;
        const [getStorageSetting] = React.useState<ModelCustomSetting.Idata | undefined>(this.storageSettingData.current);
        this.getStorageSetting = getStorageSetting;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelDocument.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getValidationForm, setValidationForm] = React.useState<ModelDocument.Idata | undefined>(this.validationForm.current);
        this.getValidationForm = getValidationForm;
        this.setValidationForm = setValidationForm;

        const [getIsDialogOpen, setIsDialogOpen] = React.useState(false);
        this.getIsDialogOpen = getIsDialogOpen;
        this.setIsDialogOpen = setIsDialogOpen;

        const [getUrlFile, setUrlFile] = React.useState<string | undefined>(undefined);
        this.getUrlFile = getUrlFile;
        this.setUrlFile = setUrlFile;

        const [getDataMain, setDataMain] = React.useState<ModelDocument.Idata | undefined>(this.dataMain.current);
        this.getDataMain = getDataMain;
        this.setDataMain = setDataMain;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getElementClientIndustry, setElementClientIndustry] = React.useState<HTMLInputElement | null>(null);
        this.getElementClientIndustry = getElementClientIndustry;
        this.setElementClientIndustry = setElementClientIndustry;

        const [getElementReportingYear, setElementReportingYear] = React.useState<HTMLInputElement | null>(null);
        this.getElementReportingYear = getElementReportingYear;
        this.setElementReportingYear = setElementReportingYear;

        const [getElementClientIndustryValue, setElementClientIndustryValue] = React.useState<string>("");
        this.getElementClientIndustryValue = getElementClientIndustryValue;
        this.setElementClientIndustryValue = setElementClientIndustryValue;

        const [getElementFunctionValue, setElementFunctionValue] = React.useState<string[]>(Array(10).fill(""));
        this.getElementFunctionValue = getElementFunctionValue;
        this.setElementFunctionValue = setElementFunctionValue;

        const [getElementRisk1Value, setElementRisk1Value] = React.useState<string>("");
        this.getElementRisk1Value = getElementRisk1Value;
        this.setElementRisk1Value = setElementRisk1Value;

        const [getElementRisk2Value, setElementRisk2Value] = React.useState<string>("");
        this.getElementRisk2Value = getElementRisk2Value;
        this.setElementRisk2Value = setElementRisk2Value;

        const [getElementRisk3Value, setElementRisk3Value] = React.useState<string>("");
        this.getElementRisk3Value = getElementRisk3Value;
        this.setElementRisk3Value = setElementRisk3Value;

        const [getElementRisk4Value, setElementRisk4Value] = React.useState<string>("");
        this.getElementRisk4Value = getElementRisk4Value;
        this.setElementRisk4Value = setElementRisk4Value;

        const [getElementRisk5Value, setElementRisk5Value] = React.useState<string>("");
        this.getElementRisk5Value = getElementRisk5Value;
        this.setElementRisk5Value = setElementRisk5Value;

        const [getElementRisk6Value, setElementRisk6Value] = React.useState<string>("");
        this.getElementRisk6Value = getElementRisk6Value;
        this.setElementRisk6Value = setElementRisk6Value;

        const [getElementRisk7Value, setElementRisk7Value] = React.useState<string>("");
        this.getElementRisk7Value = getElementRisk7Value;
        this.setElementRisk7Value = setElementRisk7Value;

        const [getElementRisk8Value, setElementRisk8Value] = React.useState<string>("");
        this.getElementRisk8Value = getElementRisk8Value;
        this.setElementRisk8Value = setElementRisk8Value;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            this.isAtTest.current = this.detectIsAtTest();

            HelperSrc.domObserver(".view_document_preview", (): void => {
                this.initializeHtmlElement();
            });

            const historyState = window.history.state as Record<string, unknown> | undefined;
            const parameterList = historyState ? (historyState.parameterList as ModelDocument.IparameterList) : undefined;

            if (parameterList) {
                this.dataMain.current = parameterList.temporaryData ? parameterList.temporaryData : (parameterList as unknown as ModelDocument.Idata);

                this.apiPreviewData(parameterList.temporaryData);
            }

            this.apiValidationForm();

            HelperSrc.storageEventListener("localization", () => {
                this.apiValidationForm();
            });
        }, []);

        React.useEffect(() => {
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageDocumentPreviewData.current = this.storeDocumentPreview.storageGet();
            this.storageSettingData.current = this.storeSetting.storageGet();
        }, [this.storageRouteData.current, this.storageDocumentPreviewData.current, this.storageSettingData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption();
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    clickUpdatePreview = (): void => {
        if (this.dataMain.current) {
            this.updateData();

            this.setApiLoadingList((prev) => ({
                ...prev,
                create: true
            }));

            ApiDocument.updateData(this.dataMain.current, (response) => {
                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        create: false
                    }));

                    return;
                }

                const result = response.data.result as unknown as ModelDocument.Istatus;

                this.apiCheckTaskStatus(result);
            });
        }
    };

    clickDownload = (): void => {
        if (this.dataMain.current) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                create: true
            }));

            ApiDocument.downloadDocx(this.dataMain.current.excel_file_name, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    create: false
                }));

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                const result = response.data.result as unknown as ModelDocument.Idownload;

                if (Object.entries(result).length > 0) {
                    HelperSrc.downloadFromBrowser(result.file, result.filename);

                    this.controllerAlert.show("success", response.message.success);
                }
            });
        }
    };

    clickDialogDraftCancel = (): void => {
        this.elementButtonDraft.current?.classList.remove("hidden");

        const storagePreferenceData = {
            ...this.storagePreferenceData.current,
            isShowPreviewDraftDialog: false,
            isAutoSaveDraft: false
        } as ModelCustomPreference.Idata;

        this.storePreference.storageSet(storagePreferenceData);

        this.setIsDialogOpen(false);
    };

    clickDialogDraftConfirm = (): void => {
        this.elementButtonDraft.current?.classList.add("hidden");

        const storagePreferenceData = {
            ...this.storagePreferenceData.current,
            isShowPreviewDraftDialog: false,
            isAutoSaveDraft: true
        } as ModelCustomPreference.Idata;

        this.storePreference.storageSet(storagePreferenceData);

        this.setIsDialogOpen(false);

        if (this.dataMain.current) {
            this.updateData();

            this.storeDocumentPreview.storageSet(this.dataMain.current);
        }

        this.autoSaveDraft();
    };

    clickBack = (): void => {
        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.documentList.path);
        }
    };

    clickNext = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        const element = event.target as HTMLElement;

        const checkForm = HelperSrc.checkForm(element, this.validationForm.current);

        if (!checkForm) {
            return;
        }

        this.updateData();

        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(
                this.storageRouteData.current.documentConfirm.path,
                this.dataMain.current as unknown as Record<string, unknown>
            );
        }
    };

    clickEnableDraft = (): void => {
        if (this.timeoutDraft) {
            clearTimeout(this.timeoutDraft);
        }

        if (this.isAtTest.current) {
            this.clickDialogDraftCancel();

            return;
        }

        const storagePreferenceData = {
            ...this.storagePreferenceData.current,
            isShowPreviewDraftDialog: false,
            isAutoSaveDraft: false
        } as ModelCustomPreference.Idata;

        this.storePreference.storageSet(storagePreferenceData);

        this.isDialogOpened.current = true;

        this.setIsDialogOpen(true);
    };

    changeDescription = (value: string, className: string): void => {
        if (this.selectOption.current) {
            const result = HelperSrc.readSelectOptionData(this.selectOption.current.risk, value, "value");

            const elementParent = document.querySelector<HTMLElement>(className);

            if (elementParent) {
                const elementDescriptionList = elementParent.querySelectorAll<HTMLInputElement>(".field_value");

                if (elementDescriptionList) {
                    const elementTextarea = elementDescriptionList[2].querySelector<HTMLTextAreaElement>("textarea[name='value']");

                    if (elementTextarea) {
                        elementTextarea.value = result.description || "";
                        elementTextarea.textContent = result.description || "";

                        elementTextarea.style.height = "auto";
                        elementTextarea.style.height = `${elementTextarea.scrollHeight - 20}px`;
                    }
                }
            }
        }
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementDetail.current = document.querySelector<HTMLElement>(".detail");
            this.elementBottom.current = document.querySelector<HTMLElement>(".bottom");

            if (this.elementDetail.current) {
                this.elementClientName.current = this.elementDetail.current.querySelector<HTMLInputElement>("[name='client_name']");
                this.elementClientIndustry.current = this.elementDetail.current.querySelector<HTMLInputElement>("[name='client_industry']");
                this.elementProjectName.current = this.elementDetail.current.querySelector<HTMLInputElement>("[name='project_name']");
                this.elementReportingYear.current = this.elementDetail.current.querySelector<HTMLInputElement>("[name='reporting_year']");

                this.setElementClientIndustry(this.elementClientIndustry.current);
                this.setElementReportingYear(this.elementReportingYear.current);
            }

            this.elementButtonDraft.current = document.querySelector<HTMLElement>(".draft");
            this.isAtTest.current = this.detectIsAtTest();

            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_document_preview");
        }
    };

    private apiValidationForm = (): void => {
        ApiDocument.validationForm((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.validationForm.current = response.data.result as unknown as ModelDocument.Idata;
            this.setValidationForm(this.validationForm.current);

            if (this.elementBottom.current) {
                HelperSrc.validationFormHelper(this.elementBottom.current, this.validationForm.current);
            }
        });
    };

    private apiPreviewData = (temporaryData: ModelDocument.Idata): void => {
        if (this.dataMain.current) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                create: true
            }));

            ApiDocument.previewData(this.dataMain.current.id, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    create: false
                }));

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                if (this.dataMain.current && !temporaryData) {
                    const result = response.data.result.preview_data as ModelDocument.Idata;

                    this.dataMain.current = {
                        ...this.dataMain.current,
                        client_industry: result.client_industry,
                        client_name: result.client_name,
                        input_file_name: result.input_file_name,
                        docx_filename: result.docx_filename,
                        excel_file_name: result.excel_file_name,
                        file: result.file,
                        section_data: result.section_data,
                        project_name: result.project_name,
                        reporting_year: result.reporting_year,
                        template_language: result.template_language,
                        template_type: result.template_type
                    };
                }

                this.setDataMain(this.dataMain.current);

                this.apiSelectOption();

                this.showFile();

                this.autoSaveDraft();
            });
        }
    };

    private apiCheckTaskStatus = (result: ModelDocument.Istatus): void => {
        const timeout = setTimeout(() => {
            clearTimeout(timeout);

            ApiDocument.checkTaskStatus(result.task_name, (responseStatus) => {
                if (responseStatus instanceof Error || responseStatus.message.error) {
                    if (responseStatus instanceof Error) {
                        HelperSrc.writeLog("error", responseStatus.message);
                    } else {
                        this.controllerAlert.show("error", responseStatus.message.error, false);
                    }

                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        create: false
                    }));

                    return;
                }

                const resultStatus = responseStatus.data.result as unknown as ModelDocument.Istatus;

                if (Object.entries(resultStatus).length > 0) {
                    ApiDocument.downloadPdf(resultStatus.download_path, (responseUpdate) => {
                        this.setApiLoadingList((prev) => ({
                            ...prev,
                            create: false
                        }));

                        if (responseUpdate instanceof Error || responseUpdate.message.error) {
                            if (responseUpdate instanceof Error) {
                                HelperSrc.writeLog("error", responseUpdate.message);
                            } else {
                                this.controllerAlert.show("error", responseUpdate.message.error, false);
                            }

                            return;
                        }

                        this.controllerAlert.show("success", responseUpdate.message.success);

                        const updateResult = responseUpdate.data.result as unknown as ModelDocument.Idata;

                        if (this.dataMain.current) {
                            this.dataMain.current.file = updateResult.file;

                            this.setDataMain(this.dataMain.current);

                            this.showFile();
                        }
                    });
                } else {
                    this.apiCheckTaskStatus(result);
                }
            });
        }, HelperSrc.STATUS_TIMEOUT);
    };

    private showFile = (): void => {
        if (this.dataMain.current && this.dataMain.current.file) {
            const decodedData = window.atob(this.dataMain.current.file);
            const uint8Array = new Uint8Array(decodedData.length);

            for (let a = 0; a < decodedData.length; a++) {
                uint8Array[a] = decodedData.charCodeAt(a);
            }

            const blob = new Blob([uint8Array], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);

            this.setUrlFile(url);
        }
    };

    private autoSaveDraft = (): void => {
        this.timeoutDraft = setTimeout(() => {
            if (this.timeoutDraft) {
                clearTimeout(this.timeoutDraft);
            }

            if (
                !this.isDialogOpened.current &&
                this.storagePreferenceData.current &&
                this.storagePreferenceData.current.isShowPreviewDraftDialog &&
                this.storagePreferenceData.current.isAutoSaveDraft
            ) {
                if (this.isAtTest.current) {
                    this.clickDialogDraftCancel();

                    return;
                }

                this.isDialogOpened.current = true;

                this.setIsDialogOpen(true);
            } else {
                if (this.storagePreferenceData.current && this.storagePreferenceData.current.isAutoSaveDraft) {
                    if (this.dataMain.current) {
                        this.updateData();

                        this.storeDocumentPreview.storageSet(this.dataMain.current);

                        this.autoSaveDraft();
                    }
                }
            }
        }, HelperSrc.DOCUMENT_PREVIEW_DRAFT_TIMEOUT);
    };

    private readSectionData = (keyName: string, selectOption?: ModelHelperSrc.IelementOption): void => {
        const elementParent = document.querySelector<HTMLElement>(`.${keyName}`);

        if (elementParent) {
            const elementContainerList = elementParent.querySelectorAll<HTMLElement>(".container");

            for (const [key, element] of Object.entries(elementContainerList)) {
                const elementValue = element.querySelector<HTMLInputElement>("[name='value']");

                if (elementValue) {
                    let value = elementValue.value;

                    const elementSelectParent = HelperSrc.findElementParent(elementValue, "container");

                    if (selectOption) {
                        if (elementSelectParent) {
                            const elementSelect = elementSelectParent.querySelector<HTMLInputElement>(".MuiSelect-root");

                            if (elementSelect) {
                                value = HelperSrc.readSelectOptionData(selectOption, elementSelect.textContent || "").display_name;
                            }
                        }
                    }

                    if (this.dataMain.current) {
                        const sectionData = this.dataMain.current.section_data;

                        if (!keyName.includes("function_") && !keyName.includes("risk_")) {
                            const index = parseInt(key) + 1;

                            const section = (sectionData as unknown as Record<string, ModelDocument.Isection[]>)[keyName][index];
                            section.value = value;
                        } else if (keyName.includes("function_") && elementSelectParent) {
                            let index = parseInt(keyName.replace("function_", ""));

                            if (index > 1) {
                                index = index * 2 - 1;
                            }

                            const sectionA = (sectionData as unknown as Record<string, ModelDocument.Isection[]>)["functions"][index];
                            sectionA.value = value;

                            const elementInput = elementSelectParent.querySelector<HTMLInputElement>("textarea");

                            if (elementInput) {
                                index++;

                                const sectionB = (sectionData as unknown as Record<string, ModelDocument.Isection[]>)["functions"][index];
                                sectionB.value = elementInput.value;
                            }
                        } else if (keyName.includes("risk_") && elementSelectParent) {
                            const index = parseInt(key) + 1;

                            if (index > 0) {
                                const sectionA = (sectionData as unknown as Record<string, ModelDocument.Isection[]>)[keyName][1];
                                sectionA.value = value;

                                const elementInput = elementSelectParent.querySelector<HTMLInputElement>("textarea");

                                if (elementInput) {
                                    const sectionB = (sectionData as unknown as Record<string, ModelDocument.Isection[]>)[keyName][2];
                                    sectionB.value = elementInput.value;
                                }
                            }
                        }
                    }
                }
            }
        }
    };

    private updateData = (): void => {
        if (
            this.dataMain.current &&
            this.elementClientName.current &&
            this.elementClientIndustry.current &&
            this.elementProjectName.current &&
            this.elementReportingYear.current &&
            this.selectOption.current
        ) {
            this.dataMain.current.client_name = this.elementClientName.current.value as string & ModelHelperSrc.IvalidationForm;
            this.dataMain.current.client_industry = this.elementClientIndustry.current.value as string & ModelHelperSrc.IvalidationForm;
            this.dataMain.current.project_name = this.elementProjectName.current.value as string & ModelHelperSrc.IvalidationForm;
            this.dataMain.current.reporting_year = this.elementReportingYear.current.value as string & ModelHelperSrc.IvalidationForm;

            this.readSectionData("basic_info");
            this.readSectionData("company_1");
            this.readSectionData("international_company_1");
            this.readSectionData("company_group");
            this.readSectionData("industry_analysis");
            this.readSectionData("analysis_targeting_international_transaction_1");
            this.readSectionData("analysis_targeting_international_transaction_2");
            this.readSectionData("analysis_targeting_international_transaction_3");
            this.readSectionData("other_transaction_tnmm");
            this.readSectionData("covered_transaction");
            this.readSectionData("other_transaction");
            this.readSectionData("function_1", this.selectOption.current.function);
            this.readSectionData("function_2", this.selectOption.current.function);
            this.readSectionData("function_3", this.selectOption.current.function);
            this.readSectionData("function_4", this.selectOption.current.function);
            this.readSectionData("function_5", this.selectOption.current.function);
            this.readSectionData("function_6", this.selectOption.current.function);
            this.readSectionData("function_7", this.selectOption.current.function);
            this.readSectionData("function_8", this.selectOption.current.function);
            this.readSectionData("function_9", this.selectOption.current.function);
            this.readSectionData("function_10", this.selectOption.current.function);
            this.readSectionData("risk_1", this.selectOption.current.risk);
            this.readSectionData("risk_2", this.selectOption.current.risk);
            this.readSectionData("risk_3", this.selectOption.current.risk);
            this.readSectionData("risk_4", this.selectOption.current.risk);
            this.readSectionData("risk_5", this.selectOption.current.risk);
            this.readSectionData("risk_6", this.selectOption.current.risk);
            this.readSectionData("risk_7", this.selectOption.current.risk);
            this.readSectionData("risk_8", this.selectOption.current.risk);
            this.readSectionData("economic_analysis_population");
            this.readSectionData("economic_analysis");
            this.readSectionData("asset_analysis");
            this.readSectionData("transfer_pricing_estimation");
        }
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        const templateLanguage = this.dataMain.current ? this.dataMain.current.template_language : "";

        ApiDocument.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.selectOption.current = response.data.result as unknown as ModelDocument.IselectOption;

            this.setSelectOption(this.selectOption.current);
        }, templateLanguage);
    };
}
